import type { useTranslations } from 'next-intl';

/**
 * Statuses that render a spinner + in-flight badge. One definition, because
 * RecordingList and RecordingPlayer had two hand-maintained `||` chains that
 * already disagreed with each other's ordering and with the store's
 * IN_FLIGHT_STATUSES — the set the liveness watchdog uses. They must stay the
 * same set: a row that spins with no watchdog can spin forever, and a row that
 * is watched but shows no spinner looks finished while it is not.
 */
const SPINNER_STATUSES: ReadonlySet<string> = new Set([
  'pending',
  'queued',
  'uploading',
  'extracting',
  'transcribing',
  'processing',
]);

/** True when the status should render the spinner + in-flight badge. */
export function showsSpinner(status: string): boolean {
  return SPINNER_STATUSES.has(status);
}

/**
 * Human-readable, LOCALISED label for an in-flight recording status.
 *
 * Both list surfaces used to render `{rec.status}` — the raw wire value — so
 * `transcribing` / `pending` / `queued` showed in English in all four locales.
 * That went unnoticed while every status badge was untranslated, and became
 * visibly wrong the moment `cancelled` got a catalog key: a German user saw
 * "abgebrochen" sitting next to "transcribing".
 *
 * Lives here rather than in either component because RecordingList and
 * RecordingPlayer render the same badge from the same `useTranslations(
 * 'recordings')` scope, and a second copy is how the two drift.
 *
 * Unknown statuses fall back to the raw value: a status the catalog has never
 * heard of is better shown verbatim than swallowed into a blank badge.
 */
export function statusBadgeLabel(status: string, t: ReturnType<typeof useTranslations>): string {
  switch (status) {
    case 'extracting':
    case 'pending':
    case 'processing':
    case 'queued':
    case 'transcribing':
    case 'uploading':
      return t(`statusBadge.${status}`);
    default:
      return status;
  }
}
